import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Truck, Users, DollarSign, MessageSquare, PackageCheck, Clock, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";

const Stat = ({ icon: Icon, label, value, sub, testId }) => (
  <div className="border border-zinc-200 bg-white p-6" data-testid={testId}>
    <div className="flex items-start justify-between">
      <div>
        <div className="mono-label">{label}</div>
        <div className="text-4xl font-bold tracking-tight mt-2 font-mono">{value}</div>
        {sub && <div className="mono-label mt-2">{sub}</div>}
      </div>
      <Icon className="h-6 w-6 text-[#0056B3] stroke-[1.5]" />
    </div>
  </div>
);

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, j] = await Promise.all([api.get("/stats/overview"), api.get("/jobs")]);
        setStats(s.data);
        setRecent(j.data.slice(0, 8));
      } catch {}
    })();
  }, []);

  if (!stats) return <div className="mono-label">Loading dashboard...</div>;

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Truck} label="Active jobs" value={stats.active_jobs} sub={`Total ${stats.total_jobs}`} testId="stat-active" />
        <Stat icon={Clock} label="New requests" value={stats.requested_jobs} sub="Awaiting quote" testId="stat-requested" />
        <Stat icon={PackageCheck} label="Delivered today" value={stats.delivered_today} testId="stat-delivered" />
        <Stat icon={DollarSign} label="Unpaid AR" value={`$${stats.unpaid_total.toLocaleString()}`} sub={`${stats.unpaid_count} invoices`} testId="stat-unpaid" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Users} label="Customers" value={stats.customers} testId="stat-customers" />
        <Stat icon={MessageSquare} label="Unread messages" value={stats.unread_messages} testId="stat-unread" />
      </div>

      <div className="border border-zinc-200 bg-white">
        <div className="flex items-center justify-between p-5 border-b border-zinc-200">
          <div>
            <div className="mono-label">RECENT JOBS</div>
            <h2 className="text-lg font-semibold mt-1">Latest activity</h2>
          </div>
          <Link to="/admin/jobs" className="text-sm text-[#0056B3] inline-flex items-center">
            View all <ArrowRight className="h-4 w-4 ml-1" />
          </Link>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 mono-label border-b border-zinc-200">
            <tr>
              <th className="text-left py-3 px-5">Job #</th>
              <th className="text-left py-3 px-5">Customer</th>
              <th className="text-left py-3 px-5">Lane</th>
              <th className="text-left py-3 px-5">PO #</th>
              <th className="text-left py-3 px-5">Status</th>
            </tr>
          </thead>
          <tbody>
            {recent.map((j, i) => (
              <tr key={j.id} className={i % 2 ? "bg-zinc-50" : ""} data-testid={`recent-job-${j.job_number}`}>
                <td className="py-3 px-5 font-mono">
                  <Link to={`/admin/jobs/${j.id}`} className="hover:underline">{j.job_number}</Link>
                </td>
                <td className="py-3 px-5">{j.customer_company || j.customer_name}</td>
                <td className="py-3 px-5 text-zinc-600">{j.pickup?.city}, {j.pickup?.state} → {j.delivery?.city}, {j.delivery?.state}</td>
                <td className="py-3 px-5 font-mono text-zinc-600">{j.po_number || "—"}</td>
                <td className="py-3 px-5"><StatusBadge status={j.status} /></td>
              </tr>
            ))}
            {recent.length === 0 && (
              <tr><td colSpan={5} className="p-8 text-center text-zinc-500 mono-label">No jobs yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
